import { useEffect, useState, type ReactNode } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import type { ICampaign, IInfluencer } from '../../redux/buzzbridge/types';
import { campaignSendInvitesUrl } from '../../configurations/api/url';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_LUXE_ERROR,
} from '../../pages/BrandDashboard/brandDashboardTokens';

export type InviteCampaignTarget =
    | IInfluencer
    | {
          _id?: string;
          name?: string;
          stage_name?: string;
          user?: { firstName?: string; lastName?: string };
      };

export interface InfluencerInviteToCampaignModalProps {
    open: boolean;
    target: InviteCampaignTarget | null;
    campaigns: ICampaign[];
    loadingCampaigns: boolean;
    token: string;
    onClose: () => void;
    title?: string;
    emptyMessage?: ReactNode;
}

function displayNameForTarget(target: InviteCampaignTarget | null): string {
    if (!target) return '';
    const t = target as {
        name?: string;
        stage_name?: string;
        user?: { firstName?: string; lastName?: string };
    };
    const fromUser = [t.user?.firstName, t.user?.lastName].filter(Boolean).join(' ');
    return t.name || t.stage_name || fromUser || 'Unknown';
}

export function InfluencerInviteToCampaignModal({
    open,
    target,
    campaigns,
    loadingCampaigns,
    token,
    onClose,
    title = 'Invite to campaign',
    emptyMessage = 'No campaigns found. Create one first.',
}: InfluencerInviteToCampaignModalProps) {
    const [statusMap, setStatusMap] = useState<Record<string, 'idle' | 'loading' | 'done' | 'error'>>({});

    useEffect(() => {
        setStatusMap({});
    }, [target, open]);

    const handleInvite = async (campaign: ICampaign) => {
        if (!target?._id || !campaign._id) return;
        setStatusMap(prev => ({ ...prev, [campaign._id]: 'loading' }));
        try {
            await axios.post(
                campaignSendInvitesUrl(campaign._id),
                { influencer_ids: [target._id] },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setStatusMap(prev => ({ ...prev, [campaign._id]: 'done' }));
        } catch {
            setStatusMap(prev => ({ ...prev, [campaign._id]: 'error' }));
        }
    };

    const displayName = displayNameForTarget(target);

    return (
        <Dialog
            open={open && Boolean(target)}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { bgcolor: '#1a1919', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)' } }}>
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2.5,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: '#fff' }}>
                            {title}
                        </Typography>
                        {target && (
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: '#adaaaa', mt: 0.25 }}>
                                {displayName}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {loadingCampaigns ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : campaigns.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                        {typeof emptyMessage === 'string' ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: 'rgba(255,255,255,0.55)',
                                }}>
                                {emptyMessage}
                            </Typography>
                        ) : (
                            emptyMessage
                        )}
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 420, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                        {campaigns.map((campaign, i) => {
                            const status = statusMap[campaign._id] ?? 'idle';
                            return (
                                <Box key={campaign._id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            px: 3,
                                            py: 2,
                                        }}>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}
                                                noWrap>
                                                {campaign.title || `Campaign ${i + 1}`}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 11,
                                                    color: '#adaaaa',
                                                    textTransform: 'capitalize',
                                                    mt: 0.25,
                                                }}>
                                                {campaign.status ?? 'draft'}
                                            </Typography>
                                        </Box>
                                        {status === 'done' ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CheckCircleOutlineIcon
                                                    sx={{ fontSize: 18, color: BRAND_LUXE_SECONDARY }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 13,
                                                        color: BRAND_LUXE_SECONDARY,
                                                    }}>
                                                    Invited
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Button
                                                size="small"
                                                disabled={status === 'loading'}
                                                onClick={() => handleInvite(campaign)}
                                                sx={{
                                                    flexShrink: 0,
                                                    borderRadius: 999,
                                                    textTransform: 'none',
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 13,
                                                    px: 2,
                                                    color: '#392700',
                                                    bgcolor: status === 'error' ? BRAND_LUXE_ERROR : BRAND_LUXE_PRIMARY,
                                                    minWidth: 80,
                                                    '&:hover': { bgcolor: '#feb700' },
                                                    '&.Mui-disabled': {
                                                        bgcolor: 'rgba(255,201,101,0.3)',
                                                        color: 'rgba(0,0,0,0.4)',
                                                    },
                                                }}>
                                                {status === 'loading' ? (
                                                    <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                                ) : status === 'error' ? (
                                                    'Retry'
                                                ) : (
                                                    'Invite'
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                    {i < campaigns.length - 1 && (
                                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mx: 3 }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
